import {createRef, useContext, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import Input from '../../../base_components/Input';
import Select from '../../../base_components/select/Select';
import {addMicroservice} from '../../../features/microservices.slice';
import Context from '../Context';
import _axios from '../../../utils/axios';
import validate from '../../../utils/validator';
import {DEFAULT_STATUS, types} from '../../../utils/settings';
import _kc from '../../../utils/keycloak';

import buttonStyles from '../../../styles/base_components/Button.module.scss';
import styles from '../../../styles/microservices/AddingMicroservice.module.scss';
import {addType} from "../../../features/types.slice";

function AddingMicroservice(props) {
  const context = useContext(Context);
  const dispatch = useDispatch();

  const username = _kc.tokenParsed.preferred_username;

  // Input
  const inputRef = createRef();
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState('');
  const [inputWarning, setInputWarning] = useState('');

  // Select versions
  const [versions, setVersions] = useState({});
  const [vPlaceholder, setVPlaceholder] = useState('');
  const [vSelectError, setVSelectError] = useState('');

  // explore the microservice versions
  const [exploring, setExploring] = useState(false);
  const [microservice, setMicroservice] = useState({});

  function add(response) {  // add the microservice to the browser storage
    const {ok, data} = response.data;
    if (typeof ok !== 'number' || typeof data !== 'object') throw new Error('The database does not correspond to our standard.');
    if (ok !== 1) throw new Error('The database is currently unavailable.');
    let err = validate([
      {value: data.id, type: 'db_microservice_id'},
      {value: data.fetchers, type: 'db_microservice_fetchers'},
      {value: data.status, type: 'db_microservice_status'},
      {value: data.host, type: 'db_microservice_host'},
      {value: data.uri, type: 'db_microservice_uri'},
      {value: data.version, type: 'db_microservice_version'},
      {value: data.author, type: 'db_microservice_author'},
      {value: data.name, type: 'db_microservice_name'},
      {value: data.created_at, type: 'db_microservice_CreatedAt'},
      {value: data.updated_at, type: 'db_microservice_UpdatedAt'}
    ]);
    err = err.filter(e => e !== '');
    if (err.length !== 0) throw new Error('Error at validating ' + data.host + ': ' + err.join('. '));

    for (let fetcher of data.fetchers) {
      for (let type in fetcher.types) {
        if (Object.keys(types).find(t => t === type)) continue;

        dispatch(addType(type));
      }
    }

    dispatch(addMicroservice(data));
  }

  function onButtonClick(explore) {  // automatic exploration
    if (inputError) return;
    let url = input.trim();
    const [urlErr] = validate([
      {value: url, type: 'url'}
    ]);
    if (urlErr) {
      setInputError(urlErr);
      setInputWarning('');
      return;
    }

    if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;

    let newMicroservice = {
      host: url
    };
    _axios.get(url)
      .then(response => {  // GET microservice
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The provided microservice does not correspond to our standard.');
        if (ok !== 1) throw new Error('The provided microservice is currently unavailable.');

        const path = data.find(object => {
          const {name, path} = object;
          return typeof name === 'string' && typeof path === 'string' && name.toLowerCase() === 'api';
        });
        if (!path) throw new Error('There is no available path to the microservice.');

        const len = url.length;
        url = (url[len - 1] === '/') ? url.slice(0, len - 1) + path.path : url + path.path;
        return _axios.get(url);
      })
      .then(response => {  // GET microservice/api
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The api ' + url + ' does not correspond to our standard.');
        if (ok !== 1) throw new Error('The api ' + url + ' is currently unavailable.');
        if (data.length === 0) throw new Error('The api ' + url + ' has no available version.');

        if (explore) {  // the user wants to list the versions
          let newVersions = {};
          for (let v of data) {
            newVersions[v.name] = false;
          }
          setVersions(newVersions);

          setMicroservice({
            host: newMicroservice.host,
            path: url,  // path to versions
            versions: data
          });
          setExploring(true);
          return;
        }

        // use the latest version
        const {name, path} = data[data.length - 1];
        if (typeof name !== 'string' || typeof path !== 'string') throw new Error('The last microservice version does not correspond to our standard.');
        newMicroservice.version = name;
        url = url + path;
        return _axios.get(url);
      })
      .then(response => {  // GET microservice/api/v1
        if (response === undefined) return;  // exploring the versions

        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The api version ' + url + ' does not correspond to our standard.');
        if (ok !== 1) throw new Error('The api version ' + url + ' is currently unavailable.');
        if (data.length === 0) throw new Error('The api version ' + url + ' has no available fetcher.');

        newMicroservice = {
          ...newMicroservice,
          status: DEFAULT_STATUS,
          name: response.headers['x-app-name'] || 'Unknown',
          uri: url,
          fetchers: data.map(fetcher => ({status: DEFAULT_STATUS, ...fetcher})),
          author: username || 'Unknown',
        };

        return _axios.post(context.role.MICROSERVICES_PATH, newMicroservice);
      })
      .then(response => {
        if (response === undefined) return;  // exploring the versions

        add(response);

        // clear the inputs
        setInput('');
      })
      .catch(handleError);
  }

  function onSubmitClick() {  // submit the explored microservice
    if (vSelectError) return;
    const [versionErr] = validate([
      {value: versions, type: 'select'}
    ]);
    if (versionErr) setVSelectError(versionErr);
    if (versionErr) return;

    const versionName = Object.keys(versions).find(vName => versions[vName]);
    const version = microservice.versions.find(({name}) => name === versionName);

    let newMicroservice = {
      host: microservice.host,
      version: versionName
    };
    let url = microservice.path + version.path;
    _axios.get(url)
      .then(response => {  // GET microservice/api/v1
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The api version ' + microservice.host + ' does not correspond to our standard.');
        if (ok !== 1) throw new Error('The api version ' + microservice.host + ' is currently unavailable.');
        if (data.length === 0) throw new Error('The api version ' + microservice.host + ' has no available fetcher.');

        newMicroservice = {
          ...newMicroservice,
          status: DEFAULT_STATUS,
          name: response.headers['x-app-name'] || 'Unknown',
          uri: url,
          fetchers: data.map(fetcher => ({status: DEFAULT_STATUS, ...fetcher})),
          author: username || 'Unknown',  // TODO set the author
        };

        return _axios.post(context.role.MICROSERVICES_PATH, newMicroservice);
      })
      .then(response => {
        add(response);

        // clear the inputs
        setExploring(false);
        setInput('');
        setVersions({});
        setVPlaceholder('');
      })
      .catch(handleError);
  }

  function onCancelClick() {  // cancel the explored microservice
    setExploring(false);
    setMicroservice({});
    setVersions({});
    setVPlaceholder('');
    setVSelectError('');
  }

  function handleError(error) {
    let message;
    if (error.response) message = 'Internal server error';
    else if (error.request) message = 'No response received';
    else if (error.message) message = error.message;
    else message = 'Unknown error';
    setInputError(message);
    setInputWarning('');
  }

  useEffect(() => {
    const timer = setTimeout(
      () => setInputWarning(input.startsWith('http://') ? 'Please use HTTPS instead.' : ''),
      500
    );
    return () => clearTimeout(timer);
  }, [input]);

  return (
    <div className={styles.addingMicroservice + ' ' + props.className}>
      <h2>Add a new microservice</h2>
      <Input
        className={styles.input}
        ref={inputRef}
        placeholder='Link to the microservice'
        disabled={exploring}
        value={input}
        setValue={setInput}
        error={inputError}
        setError={setInputError}
        warning={inputWarning}
      />

      {exploring ? (
        <>
          <Select
            className={styles.select}
            singleSelect={true}
            placeholder={vPlaceholder ? vPlaceholder : 'Enter the version'}
            setPlaceholder={setVPlaceholder}
            items={versions}
            setItems={setVersions}
            error={vSelectError}
            setError={setVSelectError}
          />

          <div className={styles.buttons}>
            <button className={buttonStyles.button} onClick={onSubmitClick}>submit</button>
            <button className={buttonStyles.button} onClick={onCancelClick}>cancel</button>
          </div>
        </>
      ) : (
        <div className={styles.buttons}>
          <button className={buttonStyles.button} onClick={() => onButtonClick(false)}>use the latest version</button>
          <button className={buttonStyles.button} onClick={() => onButtonClick(true)}>explore the versions</button>
        </div>
      )}
    </div>
  );
}

export default AddingMicroservice;